/**
 * SPEC.md §3, §4, §8 — the topbar's Layout menu, disabled since M0. M6.6
 * (issue #33) turns it into a real dropdown, mirroring `ThemeMenu.tsx`'s
 * shape. SPEC.md §4's own line for the `look: handDrawn` frontmatter
 * directive: "surface it as a toggle in the Layout menu" — this is that
 * toggle, offering the two looks §8 defines (classic / handDrawn) as a
 * global choice rather than something only settable per-diagram via
 * frontmatter.
 */
import { Check, ChevronDown, PenTool } from 'lucide-react';
import { LOOK_LABELS, LOOKS, type Look } from '../lib/mermaidRender';
import { useMenu } from './useMenu';

interface LayoutMenuProps {
  look: Look;
  onSelect: (look: Look) => void;
}

// One line selling each look, not just naming it — Excalidraw's own lesson
// (a sketchy render lowers the stakes of sharing something unpolished) is
// lost on a bare dropdown label, so this is shown under each option.
const LOOK_DESCRIPTIONS: Record<Look, string> = {
  classic: 'Sharp lines, default styling',
  handDrawn: 'Sketchy strokes — reads as a draft, not a spec',
};

export default function LayoutMenu({ look, onSelect }: LayoutMenuProps) {
  const { isOpen, containerRef, toggle, close } = useMenu<HTMLDivElement>();

  function handleSelect(next: Look) {
    onSelect(next);
    close();
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={toggle}
        className="flex items-center gap-1 rounded-sm px-2 py-1 text-xs text-text-ink hover:bg-ink-3"
      >
        <PenTool size={13} aria-hidden />
        Layout
        <ChevronDown size={12} aria-hidden />
      </button>

      {isOpen && (
        <div
          role="menu"
          aria-label="Look"
          className="absolute left-0 top-full z-10 mt-1 w-56 rounded-sm border border-ink-3 bg-ink-2 py-1 shadow-lg"
        >
          {LOOKS.map((option) => (
            <button
              key={option}
              type="button"
              role="menuitemradio"
              aria-checked={option === look}
              onClick={() => handleSelect(option)}
              className="flex w-full items-start justify-between gap-2 px-3 py-1.5 text-left text-xs text-text-ink hover:bg-ink-3"
            >
              <span>
                <span className="block">{LOOK_LABELS[option]}</span>
                <span className="mt-0.5 block text-[11px] text-mute">{LOOK_DESCRIPTIONS[option]}</span>
              </span>
              {option === look && <Check size={12} aria-hidden className="mt-0.5 shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
